import { Box, Link, Typography } from "@mui/material";
import type { JSX, ReactNode } from "react";

import type { SalesOrgLeadDetail } from "@xop/dashboard-shared";

import { contactInfo, type ContactPerson } from "../contacts.js";
import { EmailConfidenceDot } from "./badges.js";
import { CopyButton, panelInsetSx, Section } from "./primitives.js";

/* ── 2. Contact ────────────────────────────────────────────────── */

export function ContactCard({ lead }: { lead: SalesOrgLeadDetail }): JSX.Element | null {
  const info = contactInfo(lead);
  if (!info.hasAny) return null;
  const verified = info.emails.some((e) => e.confidence === "verified");
  return (
    <Section title="Contact" icon="✉" tone={verified ? "good" : "default"}>
      {info.website ? (
        <ContactRow label="Website">
          <Link
            href={`https://${info.website}`}
            target="_blank"
            rel="noreferrer"
            underline="hover"
            sx={{ color: "text.primary", textUnderlineOffset: 4, fontFamily: "ui-monospace, monospace" }}
          >
            {info.website} ↗
          </Link>
        </ContactRow>
      ) : null}
      {info.emails.length > 0 ? (
        <ContactRow label={info.emails.length === 1 ? "Email" : "Emails"}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {info.emails.map((e) => (
              <Box
                key={e.email}
                component="span"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, borderRadius: 999, border: 1, borderColor: "divider", bgcolor: "background.default", px: 1, py: 0.375, fontSize: 13 }}
              >
                {e.confidence ? <EmailConfidenceDot confidence={e.confidence} /> : null}
                <Link href={`mailto:${e.email}`} underline="none" sx={{ fontFamily: "ui-monospace, monospace", color: "text.primary" }}>
                  {e.email}
                </Link>
                <Box component="span" className="no-print">
                  <CopyButton text={e.email} label="Copy email" />
                </Box>
              </Box>
            ))}
          </Box>
        </ContactRow>
      ) : null}
      {info.phones.length > 0 ? (
        <ContactRow label={info.phones.length === 1 ? "Phone" : "Phones"}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {info.phones.map((phone) => (
              <Box
                key={phone}
                component="span"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, borderRadius: 999, border: 1, borderColor: "divider", bgcolor: "background.default", px: 1, py: 0.375, fontSize: 13 }}
              >
                <Link href={`tel:${phone.replace(/\s+/g, "")}`} underline="none" sx={{ fontFamily: "ui-monospace, monospace", color: "text.primary" }}>
                  {phone}
                </Link>
                <Box component="span" className="no-print">
                  <CopyButton text={phone} label="Copy phone" />
                </Box>
              </Box>
            ))}
          </Box>
        </ContactRow>
      ) : null}
      {info.people.length > 0 ? (
        <Box sx={{ display: "grid", gap: 0.75 }}>
          {info.people.map((p) => (
            <ContactPersonRow key={p.id} person={p} />
          ))}
        </Box>
      ) : null}
      {info.emails.length === 0 && info.phones.length === 0 && info.people.length === 0 && info.website ? (
        <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
          No named buyer or email surfaced yet — try{" "}
          <Link href={`mailto:info@${info.website}`} underline="hover" sx={{ color: "text.primary", textUnderlineOffset: 4 }}>
            info@{info.website}
          </Link>{" "}
          or the contact form on the site.
        </Typography>
      ) : null}
    </Section>
  );
}

/** A compact `label · value` row — keeps Contact a tight grid, not prose. */
function ContactRow({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { sm: "76px 1fr" }, alignItems: "baseline", gap: { xs: 0.25, sm: 1 } }}>
      <Box component="span" sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
        {label}
      </Box>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

/** One named contact, one line: name · title — email · LinkedIn · source.
 * No role-summary prose; just what you need to reach them. */
function ContactPersonRow({ person }: { person: ContactPerson }): JSX.Element {
  return (
    <Box sx={[panelInsetSx, { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, p: 1.25, fontSize: 13 }]}>
      <Box sx={{ minWidth: 0 }}>
        <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>{person.name}</Box>
        {person.title ? <Box component="span" sx={{ color: "text.secondary" }}> · {person.title}</Box> : null}
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.25, ml: { sm: "auto" } }}>
        {person.email ? (
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <Link href={`mailto:${person.email}`} underline="none" sx={{ fontFamily: "ui-monospace, monospace", color: "text.primary" }}>
              {person.email}
            </Link>
            <Box component="span" className="no-print"><CopyButton text={person.email} label="Copy email" /></Box>
          </Box>
        ) : null}
        {person.phone ? (
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <Link href={`tel:${person.phone.replace(/\s+/g, "")}`} underline="none" sx={{ fontFamily: "ui-monospace, monospace", color: "text.primary" }}>
              ☎ {person.phone}
            </Link>
            <Box component="span" className="no-print"><CopyButton text={person.phone} label="Copy phone" /></Box>
          </Box>
        ) : null}
        {person.linkedin ? (
          <Link href={person.linkedin} target="_blank" rel="noreferrer" underline="none" sx={{ color: "text.disabled", "&:hover": { color: "text.primary" } }}>LinkedIn ↗</Link>
        ) : null}
        {person.source ? (
          <Link href={person.source} target="_blank" rel="noreferrer" underline="none" sx={{ color: "text.disabled", "&:hover": { color: "text.primary" } }}>source ↗</Link>
        ) : null}
      </Box>
    </Box>
  );
}
